import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PricingDurationProps {
  price: string;
  setPrice: (price: string) => void;
  discountedPrice: string;
  setDiscountedPrice: (price: string) => void;
  duration: string;
  setDuration: (duration: string) => void;
  customDuration: string;
  setCustomDuration: (duration: string) => void;
}

export const PricingDuration = ({
  price,
  setPrice,
  discountedPrice,
  setDiscountedPrice,
  duration,
  setDuration,
  customDuration,
  setCustomDuration,
}: PricingDurationProps) => {
  const { t } = useTranslation();
  const [userCurrency, setUserCurrency] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [discountError, setDiscountError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchUserCurrency = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('currency, country')
            .eq('id', user.id)
            .single();
          
          if (error) {
            console.error("Error fetching profile:", error);
            // Default to KES
            setUserCurrency("KES");
          } else if (profile && profile.currency) {
            console.log("Found user currency:", profile.currency);
            setUserCurrency(profile.currency);
          } else if (profile && profile.country === "Ethiopia") {
            console.log("Ethiopian user, defaulting to ETB");
            setUserCurrency("ETB");
          } else if (profile && profile.country === "Kenya") {
            console.log("Kenyan user, defaulting to KES");
            setUserCurrency("KES");
          } else {
            console.log("No currency set in profile, using default KES");
            setUserCurrency("KES");
          }
        }
      } catch (error) {
        console.error("Error fetching user currency:", error);
        setUserCurrency("KES");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserCurrency();
  }, []);

  // Validate discount price whenever price or discountedPrice changes
  useEffect(() => {
    validateDiscountPrice();
  }, [price, discountedPrice]);
  
  // Function to validate discount price
  const validateDiscountPrice = () => {
    // Clear error if discounted price is empty
    if (!discountedPrice) {
      setDiscountError(null);
      return;
    }
    
    const priceValue = parseFloat(price);
    const discountedValue = parseFloat(discountedPrice);
    
    // Check if both values are valid numbers
    if (isNaN(priceValue) || isNaN(discountedValue)) {
      setDiscountError(null);
      return;
    }
    
    // Check if discounted price is higher than regular price
    if (discountedValue >= priceValue) {
      setDiscountError(t('services.discountError'));
    } else {
      setDiscountError(null);
    }
  };
  
  // Handler for price input changes
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid numbers only
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  // Handler for discounted price input changes
  const handleDiscountedPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid numbers only
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setDiscountedPrice(value);
    }
  };

  // Handler for custom duration input changes
  const handleCustomDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or valid numbers only
    if (value === '' || /^\d*$/.test(value)) {
      setCustomDuration(value);
    }
  };

  // Get the currency display label
  const getCurrencyLabel = () => {
    if (!userCurrency || userCurrency === "") {
      return "KES"; // Default to KES instead of ETB
    }
    
    return userCurrency;
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="price">{t('services.priceRequired')}</Label>
        <Input
          id="price"
          type="text"
          inputMode="decimal"
          placeholder={`0 ${getCurrencyLabel()}`}
          value={price}
          onChange={handlePriceChange}
          className="mt-1"
          disabled={isLoading}
        />
      </div>

      <div>
        <Label htmlFor="discountedPrice">{t('services.discountedPrice')}</Label>
        <Input
          id="discountedPrice"
          type="text"
          inputMode="decimal"
          placeholder={`0 ${getCurrencyLabel()}`}
          value={discountedPrice}
          onChange={handleDiscountedPriceChange}
          className={`mt-1 ${discountError ? 'border-red-500 focus:ring-red-500' : ''}`}
          disabled={isLoading}
        />
        {discountError && (
          <p className="text-red-500 text-sm mt-1">{discountError}</p>
        )}
      </div>

      <div>
        <Label htmlFor="duration">{t('services.serviceDurationLabel')}</Label>
        <Select
          value={duration}
          onValueChange={setDuration}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={t('services.selectDuration')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30-min">{t('services.thirtyMinutes')}</SelectItem>
            <SelectItem value="1-hour">{t('services.oneHour')}</SelectItem>
            <SelectItem value="2-hours">{t('services.twoHours')}</SelectItem>
            <SelectItem value="custom">{t('services.custom')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {duration === 'custom' && (
        <div>
          <Label htmlFor="customDuration">{t('services.customDurationLabel')}</Label>
          <Input
            id="customDuration"
            type="text"
            inputMode="numeric"
            placeholder="0"
            value={customDuration}
            onChange={handleCustomDurationChange}
            className="mt-1"
          />
        </div>
      )}
    </div>
  );
};
